import React, { useEffect, useRef, useState } from "react";
import { MOTION } from "../../styles/motionTokens";
import { prefersReducedMotion } from "../../styles/particleField";
import MysteryCard from "./MysteryCard";

/**
 * MatchDockTransition — the mystery card's one-way flight.
 *
 * Used twice: the map beacon's card flying down into the deck slot, and the
 * docked card lifting back out into the reveal. Both are the same move, so both
 * are the same component.
 *
 * Implementation notes that matter:
 *
 *  - The clone renders *at its destination* and is transformed back to the
 *    source, then animated to identity. Animating `transform` and `opacity`
 *    only means no layout is touched — the deck and the map underneath don't
 *    reflow while the card is in the air.
 *  - It is `aria-hidden` and never focusable: to assistive tech the card simply
 *    is where it ends up. A duplicate announced mid-flight would be a phantom.
 *  - `onDone` fires once, on a timer that is cleared on unmount, and the caller
 *    is expected to be robust to it never arriving at all (the session's phase
 *    machine has its own schedule). A dropped callback must not strand the UI.
 *  - Under reduced motion there is no flight: `onDone` fires immediately.
 */
const MatchDockTransition = ({ from, to, durationMs = MOTION.matchDockFlightMs, radius = 10, zIndex, onDone }) => {
  const [running, setRunning] = useState(false);
  const doneRef = useRef(false);
  const reduced = prefersReducedMotion();

  const valid =
    from && to && [from.x, from.y, from.width, to.x, to.y, to.width].every(Number.isFinite);

  useEffect(() => {
    doneRef.current = false;

    // No usable geometry (projection failed, deck not measured yet) or reduced
    // motion: resolve straight away rather than animating from a guessed rect.
    if (!valid || reduced) {
      onDone?.();
      doneRef.current = true;
      return undefined;
    }

    // One frame at the source before releasing, or the browser coalesces the
    // initial transform with the transition and nothing appears to move.
    const startId = window.setTimeout(() => setRunning(true), 30);
    const endId = window.setTimeout(() => {
      if (doneRef.current) return;
      doneRef.current = true;
      onDone?.();
    }, 30 + durationMs);

    return () => {
      window.clearTimeout(startId);
      window.clearTimeout(endId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, reduced, durationMs]);

  if (!valid || reduced) return null;

  const dx = from.x - to.x;
  const dy = from.y - to.y;
  const scale = to.width ? from.width / to.width : 1;

  return (
    <div
      aria-hidden="true"
      data-testid="match-dock-flight"
      style={{
        position: "absolute",
        left: to.x,
        top: to.y,
        width: to.width,
        height: to.height,
        zIndex,
        pointerEvents: "none",
        transformOrigin: "top left",
        transform: running
          ? "translate(0px, 0px) scale(1)"
          : `translate(${dx}px, ${dy}px) scale(${scale})`,
        transition: `transform ${durationMs}ms cubic-bezier(0.3,0.75,0.25,1)`,
      }}
    >
      <MysteryCard
        width={to.width}
        height={to.height}
        radius={radius}
        glyphSize={Math.round(to.width * 0.34)}
      />
    </div>
  );
};

export default MatchDockTransition;
